'use client';

import { c as _c } from "react/compiler-runtime";
import { useSubmitFeedback } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowUpRight01Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SelectField, type SelectFieldOption } from '@/components/ui/select-field';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TextareaField } from '@/components/ui/textarea-field';
import { CANNY_FEATURE_BOARD_URL, FEEDBACK_INTENT_PRESETS, FEEDBACK_MESSAGE_MAX, FEEDBACK_TYPE_OPTIONS, type FeedbackType } from '@/config/feedback';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { usePathname } from '@/i18n/navigation';
import { env } from '@/lib/config/env';
import { type DynamicTranslator, resolveApiErrorMessage } from '@/lib/errors/resolve-api-error';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { showToast } from '@/lib/ui/toast';
import { useLayoutStore } from '@/stores/layout';

/**
 * Reusable Feedback & Reports modal — bug reports, feature requests, and
 * general feedback. Mounted globally in `AppShell` (both chrome branches),
 * driven by `useLayoutStore.feedbackModalOpen`. The outer gate keeps the
 * mutation + form state out of the render path until the modal is open, and
 * gives a fresh state machine on every open.
 *
 * Follows the canonical SilkSheet shape (title + description header, actions in
 * a `ModalFooter`) used by `FaucetModal` and the settings dialogs.
 */
export function FeedbackModal() {
  const $ = _c(1);
  const open = useLayoutStore(_temp);
  if (!open) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <FeedbackModalInner />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(s) {
  return s.feedbackModalOpen;
}
function FeedbackModalInner() {
  const $ = _c(95);
  const t = useTranslations("feedback");
  const tRoot = useTranslations() as unknown as DynamicTranslator;
  const intent = useLayoutStore(_temp2);
  const context = useLayoutStore(_temp3);
  const setOpen = useLayoutStore(_temp4);
  const pathname = usePathname();
  const networkMode = useNetworkMode();
  const locale = useLocale();
  const preset = FEEDBACK_INTENT_PRESETS[intent];
  const [type, setType] = useState(preset.type);
  const [message, setMessage] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const submit = useSubmitFeedback(t0);
  let t1;
  if ($[1] !== t) {
    t1 = key => t(key as Parameters<typeof t>[0]);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const tk = t1;
  let t2;
  if ($[3] !== tk) {
    t2 = FEEDBACK_TYPE_OPTIONS.map(o => ({
      value: o.value,
      label: `${o.emoji}  ${tk(`types.${o.value}`)}`
    }));
    $[3] = tk;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const typeItems = t2;
  const isError = context?.errorDigest !== undefined || context?.errorCode !== undefined;
  let t3;
  if ($[5] !== intent || $[6] !== isError || $[7] !== t || $[8] !== tk) {
    t3 = isError ? t("error_message_label") : tk(`intent.${intent}.messageLabel`);
    $[5] = intent;
    $[6] = isError;
    $[7] = t;
    $[8] = tk;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const messageLabel = t3;
  let t4;
  if ($[10] !== message) {
    t4 = message.trim();
    $[10] = message;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const trimmed = t4;
  const canSubmit = trimmed.length > 0 && !submit.isPending;
  let t5;
  if ($[12] !== canSubmit || $[13] !== context || $[14] !== locale || $[15] !== networkMode || $[16] !== pathname || $[17] !== setOpen || $[18] !== submit || $[19] !== t || $[20] !== tRoot || $[21] !== trimmed || $[22] !== type) {
    t5 = function handleSubmit() {
      if (!canSubmit) {
        return;
      }
      const ctx = {
        route: context?.route ?? pathname,
        network: networkMode,
        locale,
        ...(context?.surface !== undefined ? {
          surface: context.surface
        } : {}),
        ...(env.NEXT_PUBLIC_APP_VERSION !== undefined ? {
          appVersion: env.NEXT_PUBLIC_APP_VERSION
        } : {}),
        ...(context?.errorDigest !== undefined ? {
          errorDigest: context.errorDigest
        } : {}),
        ...(context?.errorCode !== undefined ? {
          errorCode: context.errorCode
        } : {})
      };
      submit.mutate({
        type,
        message: trimmed,
        context: ctx
      }, {
        onSuccess: () => {
          onEvent("feedback_submitted", {
            type,
            surface: context?.surface ?? "global"
          });
          showToast({
            id: TOAST_IDS.FEEDBACK_RESULT,
            message: t("toast_success"),
            variant: "success"
          });
          setOpen(false);
        },
        onError: error => {
          showToast({
            id: TOAST_IDS.FEEDBACK_RESULT,
            message: resolveApiErrorMessage(error, tRoot) ?? t("toast_error"),
            variant: "error",
            duration: 8000
          });
        }
      });
    };
    $[12] = canSubmit;
    $[13] = context;
    $[14] = locale;
    $[15] = networkMode;
    $[16] = pathname;
    $[17] = setOpen;
    $[18] = submit;
    $[19] = t;
    $[20] = tRoot;
    $[21] = trimmed;
    $[22] = type;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const handleSubmit = t5;
  let t6;
  if ($[24] !== intent || $[25] !== isError || $[26] !== t || $[27] !== tk) {
    t6 = isError ? t("error_title") : tk(`intent.${intent}.title`);
    $[24] = intent;
    $[25] = isError;
    $[26] = t;
    $[27] = tk;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== intent || $[30] !== isError || $[31] !== t || $[32] !== tk) {
    t7 = isError ? t("error_description") : tk(`intent.${intent}.description`);
    $[29] = intent;
    $[30] = isError;
    $[31] = t;
    $[32] = tk;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== setOpen) {
    t8 = () => setOpen(false);
    $[34] = setOpen;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  const t9 = submit.isPending;
  let t10;
  if ($[36] !== t) {
    t10 = t("cancel");
    $[36] = t;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== submit.isPending || $[39] !== t10 || $[40] !== t8) {
    t11 = <AnimatedButton variant="secondary" onClick={t8} disabled={t9}>{t10}</AnimatedButton>;
    $[38] = submit.isPending;
    $[39] = t10;
    $[40] = t8;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const t12 = !canSubmit;
  let t13;
  if ($[42] !== intent || $[43] !== submit.isPending || $[44] !== t || $[45] !== tk) {
    t13 = submit.isPending ? t("sending") : tk(`intent.${intent}.cta`);
    $[42] = intent;
    $[43] = submit.isPending;
    $[44] = t;
    $[45] = tk;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== handleSubmit || $[48] !== submit.isPending || $[49] !== t12 || $[50] !== t13) {
    t14 = <AnimatedButton onClick={handleSubmit} disabled={t12} loading={submit.isPending}>{t13}</AnimatedButton>;
    $[47] = handleSubmit;
    $[48] = submit.isPending;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t11 || $[53] !== t14) {
    t15 = <ModalFooter>{t11}{t14}</ModalFooter>;
    $[52] = t11;
    $[53] = t14;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== t) {
    t16 = t("type_label");
    $[55] = t;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = v => setType(v as FeedbackType);
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== submit.isPending || $[59] !== t16 || $[60] !== type || $[61] !== typeItems) {
    t18 = <SelectField label={t16} value={type} onValueChange={t17} items={typeItems} disabled={submit.isPending} />;
    $[58] = submit.isPending;
    $[59] = t16;
    $[60] = type;
    $[61] = typeItems;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  const t19 = `intent.${intent}.placeholder`;
  let t20;
  if ($[63] !== t19 || $[64] !== tk) {
    t20 = tk(t19);
    $[63] = t19;
    $[64] = tk;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = e => setMessage(e.target.value);
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  const t22 = `${message.length} / ${FEEDBACK_MESSAGE_MAX}`;
  let t23;
  if ($[67] !== message || $[68] !== messageLabel || $[69] !== submit.isPending || $[70] !== t20 || $[71] !== t22) {
    t23 = <TextareaField label={messageLabel} required={true} value={message} maxLength={FEEDBACK_MESSAGE_MAX} rows={5} placeholder={t20} disabled={submit.isPending} onChange={t21} description={t22} />;
    $[67] = message;
    $[68] = messageLabel;
    $[69] = submit.isPending;
    $[70] = t20;
    $[71] = t22;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== context || $[74] !== isError || $[75] !== t) {
    t24 = isError && (context?.errorDigest !== undefined || context?.route !== undefined) && <div className="flex items-center gap-2 rounded-control border bg-background-secondary px-3 py-2.5 text-caption text-foreground-secondary"><span className="min-w-0 flex-1 truncate">{t("context_attached")}</span>{context?.errorDigest !== undefined && <span className="shrink-0 font-mono text-micro text-foreground-tertiary">{context.errorDigest}</span>}</div>;
    $[73] = context;
    $[74] = isError;
    $[75] = t;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== t) {
    t25 = t("canny_link");
    $[77] = t;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  let t26;
  if ($[79] !== t25) {
    t26 = <span className="min-w-0 flex-1">{t25}</span>;
    $[79] = t25;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Icon icon={ArrowUpRight01Icon} className="size-3.5 shrink-0 text-foreground-tertiary" aria-hidden="true" />;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== t26) {
    t28 = <a href={CANNY_FEATURE_BOARD_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-control border px-3 py-2.5 text-caption text-foreground-secondary transition-colors duration-fast hover:bg-background-tertiary">{t26}{t27}</a>;
    $[82] = t26;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== t18 || $[85] !== t23 || $[86] !== t24 || $[87] !== t28) {
    t29 = <div className="flex flex-col gap-4 pb-2">{t18}{t23}{t24}{t28}</div>;
    $[84] = t18;
    $[85] = t23;
    $[86] = t24;
    $[87] = t28;
    $[88] = t29;
  } else {
    t29 = $[88];
  }
  let t30;
  if ($[89] !== setOpen || $[90] !== t15 || $[91] !== t29 || $[92] !== t6 || $[93] !== t7) {
    t30 = <SilkSheet open={true} onOpenChange={setOpen} variant="detached" size="default" keyboard={true} title={t6} description={t7} footer={t15}>{t29}</SilkSheet>;
    $[89] = setOpen;
    $[90] = t15;
    $[91] = t29;
    $[92] = t6;
    $[93] = t7;
    $[94] = t30;
  } else {
    t30 = $[94];
  }
  return t30;
}
function _temp4(s_1) {
  return s_1.setFeedbackModalOpen;
}
function _temp3(s_0) {
  return s_0.feedbackContext;
}
function _temp2(s) {
  return s.feedbackIntent;
}